import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { UserX, CalendarCheck, ChevronDown, Trophy, Users, Lock, Mail, Loader2 } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { useApp, getEffectiveMatches } from "@/store/app";
import { MOCK_USERS, BETNACIONAL_FILLERS, FILLER_POINTS, MATCHES } from "@/data/matches";
import { UserAvatar } from "@/components/UserAvatar";
import { PredictionHistory } from "@/components/PredictionHistory";
import { useIsAdminUser } from "@/hooks/useAdminIds";
import { ModalShell } from "./leagues.index";
import { useUserLeagues } from "@/hooks/useLeaguesDb";
import { useProfile } from "@/hooks/useProfile";
import { useUserStats } from "@/hooks/useUserStats";
import { toast } from "sonner";

import { getBadgeBreakdown, ALL_BADGES, localizeBadge } from "@/lib/badges";
import { BadgeIcon } from "@/components/BadgeIcon";
import { useUserBadges } from "@/hooks/useUserBadges";
import { scorePrediction } from "@/lib/scoring";
import { useT, useLang } from "@/i18n";

export const Route = createFileRoute("/_app/players/$id")({
  component: PlayerProfilePage,
});

function PlayerProfilePage() {
  const { id } = useParams({ from: "/_app/players/$id" });
  const t = useT();
  const lang = useLang((s) => s.lang);
  const currentUser = useApp((s) => s.user);
  const predictions = useApp((s) => s.predictions);
  const overrides = useApp((s) => s.matchOverrides);

  // If viewing yourself, redirect-like link to your profile.
  const isSelf = currentUser?.id === id;

  const mock = MOCK_USERS.find((u) => u.id === id);
  const filler = BETNACIONAL_FILLERS.find((f) => f.id === id);
  const { data: otherUserLeagues = [] } = useUserLeagues(isSelf ? undefined : id);
  const { data: myLeagues = [] } = useUserLeagues(currentUser?.id);
  const myLeagueCodes = new Set(myLeagues.map((l) => l.code));
  // Look up real DB profile only when this isn't us, a mock, or a filler.
  const needsDbLookup = !isSelf && !mock && !filler;
  const { data: dbProfile, isLoading: profileLoading } = useProfile(
    needsDbLookup ? id : undefined
  );
  // Real-user stats always come from Supabase (works for self and others).
  const useDbStats = isSelf || (needsDbLookup && !!dbProfile);
  const { data: dbStats } = useUserStats(useDbStats ? id : undefined);
  const [showBadges, setShowBadges] = useState(false);
  const isAdmin = useIsAdminUser(mock || filler ? null : id);

  // IMPORTANT: keep all hooks above any early returns. Calling useUserBadges
  // after the `!display` return below changes hook order between renders and
  // crashes the route boundary on first navigation.
  const { data: earnedBadgeIds = [] } = useUserBadges(id);

  const matches = getEffectiveMatches(overrides);
  const finished = matches.filter((m) => m.status === "finished" && m.result);

  let display: {
    username: string;
    avatar: string;
  } | null = null;
  let totalPts = 0;
  let predictedCount = 0;
  let accuracy = 0;
  let hasPredictions = false;
  // Accuracy now comes from Supabase for real users too.

  if (isSelf && currentUser) {
    display = {
      username: currentUser.username,
      avatar: currentUser.avatar,
    };
    if (dbStats) {
      totalPts = dbStats.totalPoints;
      predictedCount = dbStats.totalPredictions;
      accuracy = dbStats.accuracy;
      hasPredictions = dbStats.totalPredictions > 0;
    }
  } else if (mock) {
    display = { username: mock.username, avatar: mock.avatar };
    const predicted = finished.filter((m) => mock.predictions[m.id]);
    predictedCount = predicted.length;
    totalPts = predicted.reduce(
      (s, m) =>
        s +
        scorePrediction(
          {
            homeScore: mock.predictions[m.id].homeScore,
            awayScore: mock.predictions[m.id].awayScore,
            firstScorer: mock.predictions[m.id].firstScorer,
          },
          m.result!,
          m.homeTeam,
          m.awayTeam
        ).total,
      0
    );
    const correct = predicted.filter((m) => {
      const p = mock.predictions[m.id];
      return (
        (p.homeScore > p.awayScore ? "H" : p.homeScore < p.awayScore ? "A" : "D") ===
        (m.result!.homeScore > m.result!.awayScore
          ? "H"
          : m.result!.homeScore < m.result!.awayScore
            ? "A"
            : "D")
      );
    }).length;
    accuracy = predicted.length > 0 ? (correct / predicted.length) * 100 : 0;
    hasPredictions = true;
  } else if (filler) {
    display = { username: filler.username, avatar: filler.avatar };
    totalPts = FILLER_POINTS[filler.id] ?? filler.points;
    hasPredictions = false;
  } else if (dbProfile) {
    display = { username: dbProfile.username, avatar: dbProfile.avatar };
    if (dbStats) {
      totalPts = dbStats.totalPoints;
      predictedCount = dbStats.totalPredictions;
      accuracy = dbStats.accuracy;
      hasPredictions = dbStats.totalPredictions > 0;
    }
  }

  if (!display) {
    if (needsDbLookup && profileLoading) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    return (
      <div className="rounded-2xl border border-dashed py-16 text-center">
        <UserX className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-3 font-semibold">{t("Player not found", "Jogador não encontrado", "Jucător negăsit")}</p>
        <div className="mt-4 flex justify-center">
          <BackButton to="/leagues" label={t("Leagues", "Ligas", "Ligi")} />
        </div>
      </div>
    );
  }

  // Badges always come from Supabase (user_badges table) — never computed
  // on the client. For mock/filler test users this returns an empty array.
  const badges = ALL_BADGES
    .filter((b) => earnedBadgeIds.includes(b.id))
    .map((b) => localizeBadge(b, lang));



  // Resolve the predictions set used for breakdowns (and thus earned dates).
  const breakdownPreds = isSelf
    ? predictions
    : mock
      ? mock.predictions
      : {};
  const breakdownMatches = getEffectiveMatches(overrides);
  const dateFmt = new Intl.DateTimeFormat(
    lang === "ro" ? "ro-RO" : "en-US",
    { day: "numeric", month: "short", year: "numeric" }
  );

  function earnedTimestampFor(badgeId: string): number | null {
    const bd = getBadgeBreakdown(badgeId, breakdownPreds, breakdownMatches);
    const matchDates = bd.contributions
      .map((c) => MATCHES.find((m) => m.id === c.matchId)?.date)
      .filter((d): d is string => !!d)
      .map((d) => new Date(d).getTime());
    if (matchDates.length === 0) {
      const finishedDates = breakdownMatches
        .filter((m) => m.status === "finished" && breakdownPreds[m.id])
        .map((m) => new Date(m.date).getTime());
      if (finishedDates.length === 0) return null;
      return Math.max(...finishedDates);
    }
    return Math.max(...matchDates);
  }
  function earnedDateFor(badgeId: string): string | null {
    const ts = earnedTimestampFor(badgeId);
    return ts === null ? null : dateFmt.format(new Date(ts));
  }

  const sortedBadges = [...badges].sort(
    (a, b) => (earnedTimestampFor(b.id) ?? 0) - (earnedTimestampFor(a.id) ?? 0)
  );

  const userLeagues = isSelf ? [] : otherUserLeagues;

  const badgesLabel = t(
    `${badges.length} badge${badges.length === 1 ? "" : "s"} earned`,
    `${badges.length} ${badges.length === 1 ? "selo" : "selos"} conquistado${badges.length === 1 ? "" : "s"}`,
    `${badges.length} ${badges.length === 1 ? "insignă obținută" : "insigne obținute"}`
  );

  return (
    <div>
      <div className="mb-4">
        <BackButton />
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent p-6">
          <div className="flex items-center gap-4">
            <div className={isAdmin ? "" : "rounded-full bg-background/50 p-1 ring-1 ring-border"}>
              <UserAvatar
                avatar={display.avatar}
                username={display.username}
                size={72}
                rounded="rounded-full"
                isAdmin={isAdmin}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl font-bold tracking-tight">
                  {display.username}
                </h1>
              </div>
              {isSelf ? (
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("This is you", "Este é você", "Acesta ești tu")}
                </div>
              ) : (
                <button
                  onClick={() => setShowBadges(true)}
                  className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary hover:bg-primary/20"
                >
                  <Trophy className="h-3 w-3" />
                  {badgesLabel}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 border-t bg-card p-4 sm:gap-3">
          <Stat label={t("Points", "Pontos", "Puncte")} value={totalPts} accent="primary" />
          <Stat
            label={t("Predictions", "Palpites", "Pronosticuri")}
            value={hasPredictions ? predictedCount : "—"}
          />
          <Stat
            label={t("Accuracy", "Precisão", "Acuratețe")}
            value={hasPredictions ? `${accuracy.toFixed(1)}%` : "—"}
            accent="success"
          />
        </div>
      </div>

      {isSelf && (
        <>
          <h2 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("Achievements", "Conquistas", "Realizări")}
          </h2>
          <div className="rounded-2xl border bg-card p-4 shadow-sm">
            {badges.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t(
                  "No badges earned yet.",
                  "Nenhum selo conquistado ainda.",
                  "Nicio insignă obținută încă."
                )}
              </p>
            ) : (
              <div className="space-y-2.5">
                {sortedBadges.map((b) => {
                  const earnedAt = earnedDateFor(b.id);
                  const bd = getBadgeBreakdown(b.id, breakdownPreds, breakdownMatches);
                  return (
                    <BadgeItem
                      key={b.id}
                      badge={b}
                      earnedAt={earnedAt}
                      contributions={bd.contributions}
                      summary={bd.summary}
                      matches={breakdownMatches}
                      dateFmt={dateFmt}
                      t={t}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {!isSelf && (
        <>
          <h2 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(
              `${display.username}'s leagues`,
              `Ligas de ${display.username}`,
              `Ligile lui ${display.username}`
            )}
          </h2>
          <div className="rounded-2xl border bg-card p-2 shadow-sm">
            {userLeagues.length === 0 ? (
              <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                {t(
                  "No leagues to show.",
                  "Nenhuma liga para mostrar.",
                  "Nu există ligi de afișat."
                )}
              </p>
            ) : (
              <ul className="space-y-2">
                {userLeagues.map((l) => {
                  const isPrivate = l.type === "private";
                  return (
                    <li
                      key={l.code}
                      className="flex items-center justify-between gap-3 rounded-xl border bg-background p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {isPrivate && (
                            <Lock className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate font-semibold">{l.name}</span>
                        </div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          {l.memberCount} {t("members", "membros", "membri")}
                        </div>
                      </div>
                      {(!isPrivate || myLeagueCodes.has(l.code)) && (
                        <Link
                          to="/leagues/$code"
                          params={{ code: l.code }}
                          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                        >
                          <Trophy className="h-3.5 w-3.5" />
                          {t("View leaderboard", "Ver classificação", "Vezi clasamentul")}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      {(isSelf || (needsDbLookup && dbProfile)) && (
        <PredictionHistory userId={id} />
      )}



      {showBadges && (
        <ModalShell
          title={t(
            `${display.username}'s badges`,
            `Selos de ${display.username}`,
            `Insignele lui ${display.username}`
          )}
          onClose={() => setShowBadges(false)}
        >
          {badges.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t(
                "No badges earned yet.",
                "Nenhum selo conquistado ainda.",
                "Nicio insignă obținută încă."
              )}
            </p>
          ) : (
            <div className="max-h-[60vh] space-y-2.5 overflow-y-auto pr-1">
              {sortedBadges.map((b) => {
                const earnedAt = earnedDateFor(b.id);
                const bd = getBadgeBreakdown(b.id, breakdownPreds, breakdownMatches);
                return (
                  <BadgeItem
                    key={b.id}
                    badge={b}
                    earnedAt={earnedAt}
                    contributions={bd.contributions}
                    summary={bd.summary}
                    matches={breakdownMatches}
                    dateFmt={dateFmt}
                    t={t}
                  />
                );
              })}
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: "primary" | "success";
}) {
  const color =
    accent === "primary"
      ? "text-primary"
      : accent === "success"
        ? "text-success"
        : "text-foreground";
  return (
    <div className="rounded-xl border bg-background p-3 text-center">
      <div className={`text-2xl font-black tracking-tight ${color}`}>{value}</div>
      <div className="mt-0.5 break-words text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">
        {label}
      </div>
    </div>
  );
}

function BadgeItem({
  badge,
  earnedAt,
  contributions,
  summary,
  matches,
  dateFmt,
  t,
}: {
  badge: { id: string; emoji: string; name: string; description: string };
  earnedAt: string | null;
  contributions: Array<{ matchId: number; homeTeam: string; awayTeam: string; label: string; pts?: number }>;
  summary?: string;
  matches: Array<{ id: number; date: string; homeTeam: string; awayTeam: string }>;
  dateFmt: Intl.DateTimeFormat;
  t: (en: string, pt: string, ro?: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const hasDetails = contributions.length > 0;
  return (
    <div className="rounded-xl border bg-background">
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        disabled={!hasDetails}
        className="flex w-full items-start gap-3 p-3 text-left disabled:cursor-default"
      >
        <BadgeIcon id={badge.id} size="md" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight">{badge.name}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{badge.description}</div>
          {earnedAt && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-success">
              <CalendarCheck className="h-3 w-3" />
              {t("Earned", "Conquistado em", "Obținută pe")} {earnedAt}
            </div>
          )}
        </div>
        {hasDetails && (
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && hasDetails && (
        <div className="border-t bg-muted/30 px-3 py-2.5">
          {summary && (
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {summary}
            </div>
          )}
          <ul className="space-y-1.5">
            {contributions.map((c, i) => {
              const match = matches.find((m) => m.id === c.matchId);
              const date = match ? dateFmt.format(new Date(match.date)) : null;
              return (
                <li
                  key={`${c.matchId}-${i}`}
                  className="flex items-start justify-between gap-2 rounded-lg bg-background p-2 text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight">
                      {c.homeTeam} <span className="text-muted-foreground">vs</span> {c.awayTeam}
                    </div>
                    <div className="mt-0.5 text-muted-foreground">{c.label}</div>
                    {date && (
                      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {date}
                      </div>
                    )}
                  </div>
                  {typeof c.pts === "number" && (
                    <div className="flex-shrink-0 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                      +{c.pts}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
